/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Prompt Templates Catalog Service & Metadata Enrichment (Task 10.2.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import {
  PROMPT_CATEGORIES_CATALOG,
  PROMPT_TEMPLATES_CATALOG,
} from '@sofia/core-ai';
import type { PromptCategoryDto, PromptTemplateDto } from '@sofia/shared-types';

export type AIModelCode = 'gpt-4o' | 'claude-3-5-sonnet' | 'gemini-1-5-pro' | 'deepseek-r1';

export interface EnrichedPromptTemplate extends PromptTemplateDto {
  category: PromptCategoryDto;
  recommendedModel: AIModelCode;
  modelLabel: string;
  creditsCost: number;
  tags: string[];
}

/**
 * Maps template categories to recommended AI models and tags.
 */
function getTemplateMetadata(
  _templateId: string,
  categoryId: string
): { recommendedModel: AIModelCode; modelLabel: string; creditsCost: number; tags: string[] } {
  // Category-level defaults
  switch (categoryId) {
    case 'pcat_coding':
      return {
        recommendedModel: 'deepseek-r1',
        modelLabel: 'DeepSeek R1',
        creditsCost: 1,
        tags: ['coding', 'developer', 'architecture', 'clean-code'],
      };
    case 'pcat_blog':
      return {
        recommendedModel: 'claude-3-5-sonnet',
        modelLabel: 'Claude 3.5 Sonnet',
        creditsCost: 2,
        tags: ['seo', 'longform', 'blog', 'content-strategy'],
      };
    case 'pcat_marketing':
      return {
        recommendedModel: 'gpt-4o',
        modelLabel: 'GPT-4o',
        creditsCost: 2,
        tags: ['ads', 'copywriting', 'conversion', 'social-media'],
      };
    case 'pcat_email':
      return {
        recommendedModel: 'gemini-1-5-pro',
        modelLabel: 'Gemini 1.5 Pro',
        creditsCost: 1,
        tags: ['email', 'outreach', 'cold-email', 'retention'],
      };
    case 'pcat_ecommerce':
      return {
        recommendedModel: 'gpt-4o',
        modelLabel: 'GPT-4o',
        creditsCost: 2,
        tags: ['ecommerce', 'amazon', 'products', 'sales-copy'],
      };
    default:
      return {
        recommendedModel: 'gpt-4o',
        modelLabel: 'GPT-4o',
        creditsCost: 1,
        tags: ['general', 'productivity'],
      };
  }
}

/**
 * Service for querying, enriching, and searching the 35+ prompt template catalog.
 */
export class PromptCatalogService {
  private static cachedTemplates: EnrichedPromptTemplate[] | null = null;

  /**
   * Returns all categories with sort order.
   */
  public static getCategories(): PromptCategoryDto[] {
    return [...PROMPT_CATEGORIES_CATALOG].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Returns all 35+ enriched prompt templates.
   */
  public static getAllTemplates(): EnrichedPromptTemplate[] {
    if (this.cachedTemplates) {
      return this.cachedTemplates;
    }

    const categoryMap = new Map<string, PromptCategoryDto>(
      PROMPT_CATEGORIES_CATALOG.map((c) => [c.id, c])
    );

    this.cachedTemplates = PROMPT_TEMPLATES_CATALOG.map((tpl) => {
      const category = categoryMap.get(tpl.categoryId) || {
        id: tpl.categoryId,
        name: 'General',
        slug: 'general',
        icon: 'folder',
        sortOrder: 99,
      };

      const meta = getTemplateMetadata(tpl.id, tpl.categoryId);

      return {
        ...tpl,
        category,
        recommendedModel: meta.recommendedModel,
        modelLabel: meta.modelLabel,
        creditsCost: meta.creditsCost,
        tags: [...meta.tags, tpl.slug.replace(/-/g, ' ')],
      };
    });

    return this.cachedTemplates;
  }

  /**
   * Find template by slug.
   */
  public static getTemplateBySlug(slug: string): EnrichedPromptTemplate | undefined {
    return this.getAllTemplates().find((t) => t.slug === slug);
  }

  /**
   * Filter templates by category, model, search query, and favorites.
   */
  public static filterTemplates(options: {
    templates?: EnrichedPromptTemplate[];
    categorySlug?: string;
    model?: string;
    searchQuery?: string;
    favoritesOnly?: boolean;
    favoriteSlugs?: string[];
    recentlyUsedSlugs?: string[];
  }): EnrichedPromptTemplate[] {
    const list = options.templates || this.getAllTemplates();
    const query = (options.searchQuery || '').trim().toLowerCase();
    const categorySlug = options.categorySlug && options.categorySlug !== 'all' ? options.categorySlug : null;
    const model = options.model && options.model !== 'all' ? options.model : null;

    return list.filter((item) => {
      // 1. Filter by category
      if (categorySlug && item.category.slug !== categorySlug) {
        return false;
      }

      // 2. Filter by AI Model
      if (model && item.recommendedModel !== model) {
        return false;
      }

      // 3. Filter by favorites
      if (options.favoritesOnly) {
        if (!options.favoriteSlugs || !options.favoriteSlugs.includes(item.slug)) {
          return false;
        }
      }

      // 4. Fuzzy text search
      if (query) {
        const matchesTitle = item.title.toLowerCase().includes(query);
        const matchesDesc = (item.description || '').toLowerCase().includes(query);
        const matchesCategory = item.category.name.toLowerCase().includes(query);
        const matchesTags = item.tags.some((t) => t.toLowerCase().includes(query));
        const matchesSlug = item.slug.toLowerCase().includes(query);

        if (!matchesTitle && !matchesDesc && !matchesCategory && !matchesTags && !matchesSlug) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Get template count per category.
   */
  public static getCategoryCounts(): Record<string, number> {
    const templates = this.getAllTemplates();
    const counts: Record<string, number> = { all: templates.length };

    for (const tpl of templates) {
      const slug = tpl.category.slug;
      counts[slug] = (counts[slug] || 0) + 1;
    }

    return counts;
  }
}
